import { AgreementResponse, IAgreement } from '../../shared/types/Agreement';
import DOMPurify from 'dompurify';
import styles from './AgreementsAcceptModal.module.scss';
import { Button, CheckboxSelections, Modal } from '@ds';
import { useState } from 'react';
import { useRespondAgreement } from 'src/shared/mutations/useRespondAgreement';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';

const getAgreementAcceptContent = (agreement?: IAgreement) => {
  const agreementName = agreement?.applicationLicenseName?.toLowerCase() || '';

  switch (agreementName) {
    case 'online services agreement':
      return {
        checkboxText: 'I have read and accept the Terms of Service',
        acceptButtonText: 'ACCEPT',
        declineButtonText: 'DECLINE & EXIT',
      };
    case 'privacy policy':
      return {
        checkboxText: 'I have read and acknowledge the Privacy Policy',
        acceptButtonText: 'ACKNOWLEDGE',
        declineButtonText: 'DECLINE & EXIT',
      };
    default:
      return {
        checkboxText: `I have read and accept the ${agreement?.applicationLicenseName || ''}`,
        acceptButtonText: 'ACCEPT',
        declineButtonText: 'DECLINE & EXIT',
      };
  }
};

interface AgreementModalProps {
  agreements: IAgreement[];
  visible: boolean;
  onModalClosed: () => void;
  onAgreementDeclined: () => void;
  onAgreementsAccepted: () => void;
}

const AgreementsAcceptModal = ({
  onAgreementsAccepted,
  onAgreementDeclined,
  agreements,
  visible,
  onModalClosed,
}: AgreementModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAgreementsAccepted, setIsAgreementsAccepted] = useState(false);
  const { mutate: respondAgreement } = useRespondAgreement();
  const currentAgreement = agreements[currentIndex];
  const title = currentAgreement?.applicationLicenseName || '';
  const agreementsLength = agreements.length;
  const { isMobile } = useScreenSize();
  const { checkboxText, acceptButtonText, declineButtonText } = getAgreementAcceptContent(currentAgreement);

  const toggleCheckbox = () => {
    setIsAgreementsAccepted((prev) => !prev);
  };

  const handleAccept = () => {
    respondAgreement({
      applicationLicenseId: currentAgreement.applicationLicenseId,
      response: AgreementResponse.Accept,
    });
    const allAgreementsAccepted = currentIndex === agreementsLength - 1;
    if (allAgreementsAccepted) {
      onAgreementsAccepted();
      onModalClosed();
    } else {
      setCurrentIndex((prev) => prev + 1);
      toggleCheckbox();
    }
  };

  const handleDecline = () => {
    respondAgreement({
      applicationLicenseId: currentAgreement.applicationLicenseId,
      response: AgreementResponse.Decline,
    });
    onAgreementDeclined();
    onModalClosed();
  };

  return (
    <Modal
      visible={visible}
      onModalClosed={handleDecline}
      className={styles['agreements-accept-modal']}
      title={title}
      modalClassName={styles['modal']}
      disableClickOutside
    >
      <div className={styles['index-indicator']}>
        {currentIndex + 1} out of {agreementsLength} agreements
      </div>
      {currentAgreement && (
        <div
          className={styles.content}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(currentAgreement.licenseContent, { ADD_ATTR: ['target'] }),
          }}
        />
      )}
      <div className="footer">
        <div className={`${styles['check-box-container']} my-[10px]`}>
          <CheckboxSelections
            className={styles['checkbox']}
            options={[{ label: '', checked: isAgreementsAccepted }]}
            onCheckboxChanged={toggleCheckbox}
          />
          <span className={styles['desc']}>{checkboxText}</span>
        </div>
        <div className="actions flex flex-col md:flex-row items-center gap-[8px]">
          <Button
            variant="contained"
            styleType="primary"
            size={isMobile ? 'xlarge' : 'large'}
            disabled={!isAgreementsAccepted}
            onClick={handleAccept}
            className="w-full md:w-[unset]"
          >
            {acceptButtonText}
          </Button>
          <Button
            className="w-full md:w-[unset]"
            variant="outlined"
            size={isMobile ? 'xlarge' : 'large'}
            styleType="secondary"
            onClick={handleDecline}
          >
            {declineButtonText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AgreementsAcceptModal;
